"use client";

// eslint-disable-next-line no-restricted-imports -- this module wraps these functions with common functionality; linting directs event tracking use cases here
import { trackEvent, trackNonInteractionEvent } from "@leafly-com/web-utils";
import memoize from "lodash/memoize";

import { Action, Category } from "@/constants/events";
import { useStore } from "@/redux/hooks";
import { getUserId } from "@/redux/selectors/user";

import { AllGtmEvents } from "../analytics/gtm";

type Dimension = {
  [name: string]: number | string | boolean | undefined | null;
};

const trackerMethods = memoize(
  (dimensions: Record<string, unknown>) => {
    return {
      publishEvent: async (
        event: AllGtmEvents & { nonInteractionEvent?: boolean },
      ) => {
        const { action, category, label, ...customDimensions } = event;

        /*
         * Non-interaction events are identified separately
         * by a different GTM Event name - which is done by calling a different function.
         * Non-interaction events are important to distinguish between because it has an impact on bounce-rate:
         * https://support.google.com/analytics/answer/1033068#NonInteractionEvents&zippy=%2Cin-this-article
         */
        const trackingFunction = event.nonInteractionEvent
          ? trackNonInteractionEvent
          : trackEvent;

        trackingFunction(category, action, label, {
          ...dimensions,
          ...customDimensions,
        });
      },
      trackEvent: async (
        category: Category,
        action: Action,
        label?: string,
        customDimensions?: Dimension,
        nonInteractionEvent = false,
      ) =>
        (nonInteractionEvent ? trackNonInteractionEvent : trackEvent)(
          category,
          action,
          label,
          {
            ...dimensions,
            ...(customDimensions || {}),
          },
        ),
      trackNonInteractionEvent: async (
        category?: Category | string | undefined,
        action?: Action | string | undefined,
        label?: string,
        customDimensions?: Record<
          string,
          number | string | boolean | undefined | null
        >,
      ) =>
        trackNonInteractionEvent(category, action, label, {
          ...dimensions,
          ...(customDimensions || {}),
        }),
    };
  },
  (dimensions) => JSON.stringify(dimensions),
);

export const useEventTracker = () => {
  return trackerMethods({
    userId: getUserId(useStore().getState()),
  });
};
